import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, MessageSquarePlus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  REVIEW_OPTIONS,
  addCollaborationComment,
  buildCollaborationState,
  fetchCollaborationState,
  reviewMeta,
  updateCollaborationState,
} from '@/lib/collaboration';

function formatRelative(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const diff = Date.now() - parsed.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function CollaborationStrip({
  matterId = '',
  targetType = '',
  targetId = '',
  targetLabel = '',
  initialState = null,
  fallbackReviewState = 'new',
  onReviewChange = null,
  disabled = false,
  compact = false,
}) {
  const { user, getAuthHeaders } = useAuth();
  const [state, setState] = useState(() => buildCollaborationState(initialState || {}, fallbackReviewState));
  const [loading, setLoading] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [savingOwner, setSavingOwner] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [ownerDraft, setOwnerDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');

  useEffect(() => {
    if (!initialState) return;
    setState(buildCollaborationState(initialState || {}, fallbackReviewState));
  }, [
    fallbackReviewState,
    initialState?.review_state,
    initialState?.reviewState,
    initialState?.collaborationReviewState,
    initialState?.comments_count,
    initialState?.owner?.label,
    initialState?.saved_decision?.updated_at,
    initialState?.updated_at,
  ]);

  useEffect(() => {
    if (!matterId || !targetType || !targetId || initialState) return;
    let cancelled = false;
    setLoading(true);
    fetchCollaborationState({
      matterId,
      targetType,
      targetId,
      headers: getAuthHeaders(),
    })
      .then((payload) => {
        if (!cancelled) setState(payload);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getAuthHeaders, initialState, matterId, targetId, targetType]);

  const effectiveMeta = useMemo(() => reviewMeta(state.review_state || fallbackReviewState), [fallbackReviewState, state.review_state]);
  const latestComment = state.comments?.[state.comments.length - 1] || null;
  const decision = state.saved_decision;

  if (!matterId || !targetType || !targetId) return null;

  const handleReviewChange = async (nextState) => {
    setSavingReview(true);
    try {
      const payload = onReviewChange
        ? await onReviewChange(nextState)
        : await updateCollaborationState({
            matterId,
            targetType,
            targetId,
            headers: getAuthHeaders(),
            body: { review_state: nextState, target_label: targetLabel || state.target_label || '' },
          });
      setState(buildCollaborationState(payload || {}, nextState));
    } catch (error) {
      toast.error(error.message || 'Unable to save review state');
    } finally {
      setSavingReview(false);
    }
  };

  const handleAssignToMe = async () => {
    setSavingOwner(true);
    try {
      const payload = await updateCollaborationState({
        matterId,
        targetType,
        targetId,
        headers: getAuthHeaders(),
        body: {
          target_label: targetLabel || state.target_label || '',
          owner: { assign_to_me: true },
        },
      });
      setState(payload);
      setShowAssign(false);
      setOwnerDraft('');
    } catch (error) {
      toast.error(error.message || 'Unable to assign owner');
    } finally {
      setSavingOwner(false);
    }
  };

  const handleAssignCustom = async () => {
    if (!ownerDraft.trim()) return;
    setSavingOwner(true);
    try {
      const payload = await updateCollaborationState({
        matterId,
        targetType,
        targetId,
        headers: getAuthHeaders(),
        body: {
          target_label: targetLabel || state.target_label || '',
          owner: { label: ownerDraft.trim(), name: ownerDraft.trim() },
        },
      });
      setState(payload);
      setShowAssign(false);
      setOwnerDraft('');
    } catch (error) {
      toast.error(error.message || 'Unable to assign owner');
    } finally {
      setSavingOwner(false);
    }
  };

  const handleClearOwner = async () => {
    setSavingOwner(true);
    try {
      const payload = await updateCollaborationState({
        matterId,
        targetType,
        targetId,
        headers: getAuthHeaders(),
        body: {
          target_label: targetLabel || state.target_label || '',
          owner: null,
        },
      });
      setState(payload);
    } catch (error) {
      toast.error(error.message || 'Unable to clear owner');
    } finally {
      setSavingOwner(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteDraft.trim()) return;
    setSavingNote(true);
    try {
      const payload = await addCollaborationComment({
        matterId,
        targetType,
        targetId,
        text: noteDraft.trim(),
        headers: getAuthHeaders(),
      });
      setState(payload);
      setNoteDraft('');
      setShowNote(false);
    } catch (error) {
      toast.error(error.message || 'Unable to save note');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div
      style={{
        marginTop: compact ? '10px' : '14px',
        padding: compact ? '10px 12px' : '12px 14px',
        borderRadius: '16px',
        border: '1px solid var(--border-subtle)',
        background: 'color-mix(in srgb, var(--bg-elevated) 72%, transparent)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: effectiveMeta.color,
              background: effectiveMeta.bg,
              padding: '5px 9px',
              borderRadius: '999px',
            }}
          >
            {effectiveMeta.label}
          </span>
          {state.owner?.label ? (
            <span
              style={{
                fontSize: '11px',
                color: 'var(--text-secondary)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                padding: '5px 9px',
                borderRadius: '999px',
              }}
            >
              Owner: {state.owner.label}
            </span>
          ) : null}
          {decision?.state ? (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Decision saved {formatRelative(decision.updated_at || decision.created_at)}
            </span>
          ) : null}
          {loading ? <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Loading review…</span> : null}
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            aria-label="Review state"
            className="advoq-input"
            value={state.review_state || 'new'}
            disabled={disabled || savingReview}
            onChange={(event) => handleReviewChange(event.target.value)}
            style={{ minWidth: compact ? '138px' : '154px', height: '36px' }}
          >
            {REVIEW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button type="button" className="app-chat-button" onClick={() => setShowAssign((value) => !value)} disabled={disabled || savingOwner}>
            <UserPlus size={12} />
            {state.owner?.label ? 'Reassign' : 'Assign'}
          </button>
          <button type="button" className="app-chat-button" onClick={() => setShowNote((value) => !value)} disabled={disabled || savingNote}>
            <MessageSquarePlus size={12} />
            Note
          </button>
        </div>
      </div>

      {latestComment ? (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.65 }}>
          <strong style={{ color: 'var(--text-primary)' }}>
            {latestComment.author?.label || 'Team note'}:
          </strong>{' '}
          {latestComment.text}
          <span style={{ color: 'var(--text-muted)' }}> · {formatRelative(latestComment.updated_at || latestComment.created_at)}</span>
        </div>
      ) : null}

      {showAssign ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="app-chat-button" onClick={handleAssignToMe} disabled={savingOwner}>
            <CheckCircle2 size={12} />
            Assign to me
          </button>
          <input
            className="advoq-input"
            value={ownerDraft}
            onChange={(event) => setOwnerDraft(event.target.value)}
            placeholder="Owner name"
            style={{ minWidth: '180px', flex: '1 1 220px' }}
          />
          <button type="button" className="app-chat-button" onClick={handleAssignCustom} disabled={savingOwner || !ownerDraft.trim()}>
            Save owner
          </button>
          {state.owner?.label ? (
            <button type="button" className="app-chat-button" onClick={handleClearOwner} disabled={savingOwner}>
              Clear
            </button>
          ) : null}
        </div>
      ) : null}

      {showNote ? (
        <div style={{ display: 'grid', gap: '8px' }}>
          <textarea
            className="advoq-input"
            rows={compact ? 2 : 3}
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder={`Internal note for ${targetLabel || 'this item'}`}
            style={{ resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="app-chat-button" onClick={handleAddNote} disabled={savingNote || !noteDraft.trim()}>
              Save note
            </button>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {user?.name || user?.email || 'You'} can leave quiet chamber notes here.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
